import { motion } from 'framer-motion';
import { springs } from '../lib/animations';

interface Strain {
  name: string;
  type?: string;
  effects?: string[];
  flavors?: string[];
  description?: string;
  rating?: number;
  image_url?: string;
}

interface StrainCardProps {
  strain: Strain;
  onClick: () => void;
}

export default function StrainCard({ strain, onClick }: StrainCardProps) {
  const typeBg = strain.type === 'Sativa'
    ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
    : strain.type === 'Indica'
    ? 'bg-purple-500/20 text-purple-300 border-purple-500/30'
    : 'bg-cannabis-500/20 text-cannabis-300 border-cannabis-500/30';

  return (
    <motion.button
      onClick={onClick}
      className="bg-cannabis-800 border border-cannabis-700 rounded-xl p-4 text-left hover:border-cannabis-500 transition w-full"
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.98 }}
      transition={springs.subtle}
    >
      {/* Thumbnail or placeholder */}
      <div className="w-full h-28 rounded-lg bg-cannabis-900 flex items-center justify-center mb-3 overflow-hidden">
        {strain.image_url ? (
          <img src={strain.image_url} alt={strain.name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-cream/30 text-sm">No image</span>
        )}
      </div>

      {/* Name + type badge */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-cream font-semibold text-sm leading-tight truncate flex-1">{strain.name}</h3>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border shrink-0 ${typeBg}`}>
          {strain.type || 'Hybrid'}
        </span>
      </div>

      {/* Rating stars */}
      {strain.rating != null && (
        <div className="flex items-center gap-1 mb-2">
          {[1, 2, 3, 4, 5].map(i => (
            <span key={i} className={`text-xs ${i <= Math.round(strain.rating! / 2) ? 'text-yellow-400' : 'text-cream/20'}`}>★</span>
          ))}
          <span className="text-cream/40 text-xs ml-1">{strain.rating.toFixed(1)}</span>
        </div>
      )}

      {/* Short effects */}
      {Array.isArray(strain.effects) && strain.effects.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {strain.effects.slice(0, 3).map((eff, i) => {
            const effStr = typeof eff === 'string' ? eff : typeof eff === 'object' && eff && typeof (eff as any).name === 'string' ? (eff as any).name : String(eff);
            return <span key={`${effStr}-${i}`} className="text-[10px] bg-cannabis-700/50 text-cream/50 px-1.5 py-0.5 rounded">{effStr}</span>;
          })}
        </div>
      )}
    </motion.button>
  );
}
